import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Timer,
  TrendingUp,
  ArrowRight,
  Bell,
  RefreshCw,
  Eye,
  ChevronRight,
  BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const SLADashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadTrendData();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/approval-flows/v2/sla-dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading SLA dashboard:', error);
      toast.error('Error cargando dashboard de SLAs');
    } finally {
      setLoading(false);
    }
  };

  const loadTrendData = async () => {
    try {
      const response = await api.get('/approval-flows/v2/sla-trend');
      setTrendData(response.data.trend || []);
    } catch (error) {
      console.error('Error loading trend data:', error);
      // Generar datos de ejemplo si no hay datos reales
      const mockData = generateMockTrendData();
      setTrendData(mockData);
    }
  };

  const generateMockTrendData = () => {
    const data = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      data.push({
        fecha: date.toISOString().split('T')[0],
        cumplimiento: Math.round(85 + Math.random() * 15),
        aprobadas: Math.floor(Math.random() * 10),
        vencidas: Math.floor(Math.random() * 2)
      });
    }
    return data;
  };

  const getEstadoSLAColor = (estado) => {
    switch (estado) {
      case 'VENCIDO': return 'bg-red-500 text-white';
      case 'CRITICO': return 'bg-red-400 text-white';
      case 'WARNING': return 'bg-amber-500 text-white';
      case 'NORMAL': return 'bg-green-500 text-white';
      default: return 'bg-slate-400 text-white';
    }
  };

  const getProgressColor = (porcentaje) => {
    if (porcentaje >= 100) return 'bg-red-500';
    if (porcentaje >= 75) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const formatHoras = (horas) => {
    if (horas === undefined || horas === null) return '-';
    if (horas < 0) return 'Vencido';
    if (horas < 1) return `${Math.round(horas * 60)} min`;
    if (horas < 24) return `${horas.toFixed(1)}h`;
    return `${Math.floor(horas / 24)}d ${Math.round(horas % 24)}h`;
  };

  const viewRequestDetail = (request) => {
    setSelectedRequest(request);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const resumen = dashboard?.resumen || {};

  return (
    <div className="p-6 space-y-6" data-testid="sla-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Timer className="text-blue-600" />
            Dashboard de SLAs
          </h1>
          <p className="text-slate-600 mt-1">
            Monitoreo de tiempos de aprobación y cumplimiento de SLAs
          </p>
        </div>
        <Button variant="outline" onClick={loadDashboard}>
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="text-blue-600" size={20} />
            <span className="text-sm font-medium text-blue-700">Pendientes</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{resumen.total_pendientes || 0}</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="text-red-600" size={20} />
            <span className="text-sm font-medium text-red-700">Vencidas</span>
          </div>
          <p className="text-3xl font-bold text-red-900">{resumen.vencidas || 0}</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-amber-600" size={20} />
            <span className="text-sm font-medium text-amber-700">Por Vencer</span>
          </div>
          <p className="text-3xl font-bold text-amber-900">{resumen.por_vencer || 0}</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-sm font-medium text-green-700">Normales</span>
          </div>
          <p className="text-3xl font-bold text-green-900">{resumen.normales || 0}</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="text-purple-600" size={20} />
            <span className="text-sm font-medium text-purple-700">Cumplimiento</span>
          </div>
          <p className="text-3xl font-bold text-purple-900">{resumen.tasa_cumplimiento_sla || 100}%</p>
        </Card>
      </div>

      {/* Gráfico de Tendencias */}
      {trendData.length > 0 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="text-blue-600" />
            Tendencia de Cumplimiento (Últimos 30 días)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCumplimiento" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis 
                  domain={[0, 100]} 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  formatter={(value, name) => {
                    if (name === 'cumplimiento') return [`${value}%`, 'Cumplimiento SLA'];
                    if (name === 'aprobadas') return [value, 'Aprobadas'];
                    if (name === 'vencidas') return [value, 'Vencidas'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="cumplimiento" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorCumplimiento)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-slate-600">Tasa de Cumplimiento SLA</span>
            </div>
          </div>
        </Card>
      )}

      {/* Solicitudes Vencidas */}
      {dashboard?.vencidas?.length > 0 && (
        <Card className="p-4 border-red-200 bg-red-50">
          <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <XCircle className="text-red-600" />
            Solicitudes Vencidas ({dashboard.vencidas.length})
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Solicitud</TableHead>
                  <TableHead>Flujo</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Tiempo Vencido</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.vencidas.map(req => (
                  <TableRow key={req.id} className="bg-white/50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.entity_nombre}</p>
                        <p className="text-xs text-slate-500">{req.entity_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>{req.flow_nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">Nivel {req.nivel_actual}/{req.niveles_totales}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-red-600 font-medium">
                        {Math.abs(req.horas_restantes || 0).toFixed(1)}h vencido
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => viewRequestDetail(req)}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Solicitudes Por Vencer */}
      {dashboard?.por_vencer?.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" />
            Próximas a Vencer ({dashboard.por_vencer.length})
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Solicitud</TableHead>
                  <TableHead>Flujo</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Tiempo Restante</TableHead>
                  <TableHead>Progreso SLA</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.por_vencer.map(req => (
                  <TableRow key={req.id} className="bg-white/50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.entity_nombre}</p>
                        <p className="text-xs text-slate-500">{req.entity_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>{req.flow_nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">Nivel {req.nivel_actual}/{req.niveles_totales}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={getEstadoSLAColor(req.estado_sla)}>
                          {formatHoras(req.horas_restantes)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <Progress 
                          value={Math.min(req.porcentaje_sla || 0, 100)} 
                          className="h-2"
                        />
                        <span className="text-xs text-slate-500">{Math.round(req.porcentaje_sla || 0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => viewRequestDetail(req)}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Solicitudes Normales */}
      {dashboard?.normales?.length > 0 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="text-green-600" />
            Solicitudes en Tiempo ({dashboard.normales.length})
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Solicitud</TableHead>
                  <TableHead>Flujo</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Tiempo Restante</TableHead>
                  <TableHead>Progreso SLA</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.normales.slice(0, 10).map(req => (
                  <TableRow key={req.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.entity_nombre}</p>
                        <p className="text-xs text-slate-500">{req.entity_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>{req.flow_nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">Nivel {req.nivel_actual}/{req.niveles_totales}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getEstadoSLAColor(req.estado_sla)}>
                        {formatHoras(req.horas_restantes)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <Progress 
                          value={Math.min(req.porcentaje_sla || 0, 100)} 
                          className="h-2"
                        />
                        <span className="text-xs text-slate-500">{Math.round(req.porcentaje_sla || 0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => viewRequestDetail(req)}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Bell className="text-blue-600 mt-1" />
          <div>
            <h4 className="font-semibold text-blue-900">Sistema de Alertas Automáticas</h4>
            <p className="text-sm text-blue-700 mt-1">
              El sistema verifica los SLAs cada 15 minutos. Cuando una solicitud está próxima a vencer 
              (según la configuración de cada flujo), se envían notificaciones automáticas a los aprobadores 
              correspondientes. Las solicitudes vencidas pueden escalar automáticamente si está configurado.
            </p>
          </div>
        </div>
      </Card>

      {/* Dialog de Detalle */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="text-blue-600" />
              Detalle de Solicitud
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Entidad</p>
                  <p className="font-medium">{selectedRequest.entity_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Tipo</p>
                  <p className="font-medium">{selectedRequest.entity_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Flujo</p>
                  <p className="font-medium">{selectedRequest.flow_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Nivel Actual</p>
                  <Badge variant="outline">
                    {selectedRequest.nivel_actual} de {selectedRequest.niveles_totales}
                  </Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Estado del SLA</p>
                <div className="flex items-center gap-4">
                  <Badge className={getEstadoSLAColor(selectedRequest.estado_sla)}>
                    {selectedRequest.estado_sla}
                  </Badge>
                  <span className="text-sm">
                    {selectedRequest.horas_restantes !== undefined && (
                      selectedRequest.horas_restantes > 0 
                        ? `${formatHoras(selectedRequest.horas_restantes)} restantes`
                        : `Vencido hace ${formatHoras(Math.abs(selectedRequest.horas_restantes))}`
                    )}
                  </span>
                </div>
                {selectedRequest.sla_horas && (
                  <div className="mt-2">
                    <Progress 
                      value={Math.min(selectedRequest.porcentaje_sla || 0, 100)} 
                      className="h-3"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      SLA configurado: {selectedRequest.sla_horas} horas
                    </p>
                  </div>
                )}
              </div>

              {selectedRequest.aprobaciones?.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-2">Historial de Aprobaciones</p>
                  <div className="space-y-2">
                    {selectedRequest.aprobaciones.map((aprobacion, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <ChevronRight size={14} className="text-slate-400" />
                        <span>Nivel {aprobacion.nivel}:</span>
                        <Badge className={aprobacion.decision === 'APROBADO' ? 'bg-green-500' : 'bg-red-500'}>
                          {aprobacion.decision}
                        </Badge>
                        <span className="text-slate-500">por {aprobacion.aprobado_por}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Información Adicional</p>
                <div className="text-sm space-y-1">
                  <p><span className="text-slate-500">Solicitado por:</span> {selectedRequest.requested_by}</p>
                  <p><span className="text-slate-500">Creado:</span> {new Date(selectedRequest.created_at).toLocaleString()}</p>
                  {selectedRequest.sla_vence_at && (
                    <p><span className="text-slate-500">SLA vence:</span> {new Date(selectedRequest.sla_vence_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SLADashboard;
